import React, { useEffect, useMemo, useState } from 'react';

import { Dropdown } from '../Dropdown';
import { Column } from '../Column';
import { Row } from '../Row';
import { ColumnGroup } from '../Column-group';
import { Paginator } from '../Paginator';
import { PaginatorTemplate } from '../Paginator/types';
import { useStyletron } from '../../Themes';
import { ISmartProps } from './types';
import { View } from './view';
import { TableWrapper, fixedPaginatorStyles } from './styles';

export { Column, Row, ColumnGroup };

export type DataTableProps = Omit<ISmartProps, 'css'>;


export const DataTable = React.forwardRef((props: DataTableProps, ref: any) => {
	const {
		columns,
		value,
		hideColumns,
		rowsPerPage = 5,
		paginator,
		paginatorTemplate = 'GoToLabel',
		alwaysShowPaginator,
		paginatorPosition = 'bottom',
		fixedPaginator,
		...rest
	} = props;

	const [selectedColumns, setSelectedColumns] = React.useState(columns);
	const [firstPage, setFirstPage] = useState<number>(0);
	const [customRows, setCustomRows] = useState(rowsPerPage);
	const [currentPage, setCurrentPage] = useState<number | undefined>(1);
	const [data, setData] = useState<any[]>([]);
	const [totalFilteredRecords, setFilteredTotalRecords] = useState<number>();
	const [css] = useStyletron();

	const onColumnToggle = (event: any) => {
		const selectedColumns = event.value;
		const orderedSelectedColumns =
			columns &&
			columns.filter((col: any) =>
				selectedColumns.some((sCol: any) => {
					return sCol.id === col.field;
				}),
			);

		setSelectedColumns(orderedSelectedColumns);
	};


	const handleChangeCurrentPage = (page: any) => {
		const getPageNumber = (inputNumber: any, totalPages: any) => {
			if (isNaN(parseInt(inputNumber))) return;
			if (inputNumber <= 0) return 1;
			if (parseInt(inputNumber) > totalPages) return totalPages;
			return inputNumber;
		};
		const newPage = paginatorTemplate === 'Toolbar' ? page?.[0]?.id : page;
		const pageNumber = getPageNumber(newPage, Math.ceil(data.length / customRows));

		setCurrentPage(pageNumber);
	};

	const onCustomPageChange = (event: any) => {
		setFirstPage(event.first);
		setCustomRows(event.rows);
		setCurrentPage(event.page + 1);
	};

	const totalRecords = useMemo(() => {
		return totalFilteredRecords ? totalFilteredRecords : data.length;
	}, [data, totalFilteredRecords]);

	const headerContent = (
		<div className={'border-bottom border-light ' + css({ padding: 'var(--epods--space-inset-s)' })}>
			<div>{props.header}</div>
			<div style={{ textAlign: 'left', width: '320px', marginLeft: 'auto', padding: 'var(--epods--space-inset-s)' }}>
				<Dropdown
					size="compact"
					icon="search"
					multi
					onChange={(e: any) => onColumnToggle(e)}
					value={selectedColumns?.map(value => {
						return { id: value.field, label: value.header };
					})}
					options={columns?.map(value => {
						return { id: value.field, label: value.header };
					})}
				/>
			</div>
		</div>
	);

	const footerContent = (
		<div className={css({ padding: 'var(--epods--space-inset-s)' })}>
			<div>{props.footer}</div>
			<div style={{ textAlign: 'left', width: '200px' }}>
				<Dropdown
					preset="multi"
					onChange={(e: any) => onColumnToggle(e)}
					value={selectedColumns?.map(value => {
						return { id: value.field, label: value.header };
					})}
					options={columns?.map(value => {
						return { id: value.field, label: value.header };
					})}
				/>
			</div>
		</div>
	);

	let newProps = { ...rest };

	if (hideColumns === 'header') {
		newProps = { ...rest, header: headerContent };
	}

	if (hideColumns === 'footer') {
		newProps = { ...rest, footer: footerContent };
	}

	const columnComponents =
		selectedColumns &&
		selectedColumns.map(col => {
			return <Column key={col.field} field={col.field} header={col.header} />;
		});

	useEffect(() => {
		setData(
			value && value.length
				? value.map(item => {
					return { ...item, toEdit: false };
				})
				: [],
		);
	}, [value]);

	const page = paginatorTemplate === 'Toolbar' ? [{ label: currentPage, id: currentPage }] : currentPage;
	const showPaginator = alwaysShowPaginator || customRows < totalRecords;

	return (
		<TableWrapper>
			{paginator && !!value?.length && showPaginator && (paginatorPosition === 'top' || paginatorPosition === 'both') && (
				<Paginator
					paginatorTemplate={paginatorTemplate as PaginatorTemplate}
					first={firstPage}
					page={page}
					rows={customRows}
					totalRecords={totalRecords}
					setCurrentPage={handleChangeCurrentPage}
					setCustomFirst={setFirstPage}
					onPageChange={onCustomPageChange}
				/>
			)}
			<View
				rootref={ref}
				value={value}
				rows={paginator ? customRows : undefined}
				onPage={() => { }}
				first={firstPage}
				onValueChange={(tableItems: any[]) => {
					setFilteredTotalRecords(tableItems?.length);
				}}
				paginator={false}
				{...newProps}
			>
				{props.children}
				{columnComponents}
			</View>
			{paginator && !!value?.length && showPaginator && (paginatorPosition === 'bottom' || paginatorPosition === 'both') && (
				<Paginator
					paginatorTemplate={paginatorTemplate as PaginatorTemplate}
					first={firstPage}
					page={page}
					rows={customRows}
					totalRecords={totalRecords}
					setCurrentPage={handleChangeCurrentPage}
					setCustomFirst={setFirstPage}
					onPageChange={onCustomPageChange}
					className={fixedPaginator ? css(fixedPaginatorStyles) : ''}
				/>
			)}
		</TableWrapper>
	);
});
